import { useState } from 'react';
import { Link } from 'react-router-dom';
import { apiFetch, getToken, clearToken } from '../dppApi';
import '../App.css';

// The account-deletion flow, extracted so it can render in two places without
// duplicating the logic: the public /delete-account page (reachable post-uninstall,
// a Play requirement) and the Profile page's "Delete account" section. This is the
// inner content only — callers supply the page/card chrome. `onCancel` wires the
// Cancel button (go back on the public page; return to a section in Profile).
//
// What deletion does is deliberately narrow, and the copy says so plainly: the
// passport's chain of custody is proof of title for a physical object and belongs
// to the issuing brand and later owners too, so ownership records survive.
// Credentials, contact details and self-uploaded documents do not.
const CONTACT_EMAIL = 'privacy@love-bloom.com';

const DeleteAccountPanel = ({ onCancel }) => {
    const token = getToken('user');
    const [confirmText, setConfirmText] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [result, setResult] = useState(null);   // { deleted_ref, documents_deleted }

    const confirmed = confirmText.trim().toUpperCase() === 'DELETE';

    const submit = async () => {
        if (!confirmed || busy) return;
        setBusy(true);
        setError(null);
        try {
            const data = await apiFetch('/api/user/account/delete', { method: 'POST', token });
            setResult(data);
            clearToken('user');   // the token is dead server-side too
        } catch (err) {
            setError(err.message);
        } finally {
            setBusy(false);
        }
    };

    if (result) {
        return (
            <>
                <h1>Account deleted</h1>
                <p>
                    Your sign-in details and contact information have been removed, along with
                    {result.documents_deleted === 1 ? ' 1 document ' : ` ${result.documents_deleted} documents `}
                    you uploaded. You can no longer sign in with this account.
                </p>
                <div className="delete-ref-box">
                    <div className="delete-ref-label">Your reference</div>
                    <div className="delete-ref-value">{result.deleted_ref}</div>
                </div>
                <p>
                    Keep this reference. It stands in for your name in any message threads with
                    brands, and identifies your record if you ever need to contact us about it.
                </p>
                <p>
                    <Link to="/">Return to Bloom</Link>
                </p>
            </>
        );
    }

    return (
        <>
            <h1>Delete your account</h1>

            <p className="settings-section-sub">This cannot be undone.</p>

            {/* The decision turns on two facts, so they lead. Note this must stay
                in step with USER_UPLOADED_DOC_TYPES in routes/users.py: only
                insurance and appraisal are removed. Receipts and certificates are
                issued by the brand and are part of the piece's record, so they
                survive — an earlier version of this page wrongly said otherwise. */}
            <div className="delete-ledger">
                <div className="delete-ledger-row">
                    <div className="delete-ledger-key">Removed</div>
                    <div className="delete-ledger-val">
                        Your sign-in and contact details, and documents you added yourself — appraisals and insurance records.
                    </div>
                </div>
                <div className="delete-ledger-row">
                    <div className="delete-ledger-key">Kept</div>
                    <div className="delete-ledger-val">
                        Your name on the ownership record of each piece you have held, and receipts and certificates issued by the brand.
                    </div>
                </div>
            </div>

            <details className="delete-why">
                <summary>Why ownership records are kept</summary>
                <p>
                    A passport is the proof of title for a physical object: it is the record
                    that a piece was sold, to whom, and when, and it belongs to the brand that
                    issued it and to anyone who owns the piece after you, not only to you.
                    Removing it would break the chain of custody that gives the piece its
                    provenance, in the same way a receipt or a title deed cannot be unwritten
                    after the fact.
                </p>
                <p>
                    So your name stays attached to the ownership records of pieces you have
                    held. Those records are visible only to the issuing brand and to subsequent
                    owners of that specific piece — never publicly.
                </p>
                <p>
                    Pieces you currently own stay yours. The passport remains with you under
                    your reference, and is unaffected by deleting your account.
                </p>
                <p>
                    Our <Link to="/privacy">Privacy Policy</Link> describes this in full.
                </p>
            </details>

            {token ? (
                <div className="delete-confirm">
                    <p className="delete-confirm-label">Type <strong>DELETE</strong> below to confirm.</p>
                    <input
                        className="dpp-input delete-confirm-input"
                        value={confirmText}
                        onChange={e => setConfirmText(e.target.value)}
                        aria-label="Type DELETE to confirm"
                        autoComplete="off"
                    />
                    {error && <p className="dpp-error">{error}</p>}
                    <div className="delete-actions">
                        <button
                            className="dpp-button danger"
                            onClick={submit}
                            disabled={!confirmed || busy}
                        >
                            {busy ? 'Deleting…' : 'Delete my account'}
                        </button>
                        {onCancel && (
                            <button className="dpp-button secondary" onClick={onCancel}>
                                Cancel
                            </button>
                        )}
                    </div>
                </div>
            ) : (
                <>
                    <h2>Sign in to continue</h2>
                    <p>
                        For your security, deletion has to be confirmed from your own account.{' '}
                        <Link to="/user">Sign in</Link>, then return to this page.
                    </p>
                    <p>
                        If you cannot sign in — for example you have lost access to your email —
                        write to <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> and we
                        will handle the request for you.
                    </p>
                </>
            )}
        </>
    );
};

export default DeleteAccountPanel;
